import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Settings, Save, Search, Check, Plus } from "lucide-react";
import { moduleApi } from "../../lib/api";

const employeeName = (employee: any) => {
  const fullName = [employee.first_name, employee.last_name].filter(Boolean).join(" ").trim();
  return fullName || employee.name || employee.email || employee.id;
};

function StaffPicker({
  label,
  selected,
  onToggle,
  onAdd,
  employees,
}: {
  label: string;
  selected: string[];
  onToggle: (name: string) => void;
  onAdd: (name: string) => void;
  employees: any[];
}) {
  const [search, setSearch] = useState("");
  const [customName, setCustomName] = useState("");
  const filtered = employees.filter((emp) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return employeeName(emp).toLowerCase().includes(term);
  });

  const handleAddCustom = () => {
    const name = customName.trim();
    if (!name) return;
    onAdd(name);
    setCustomName("");
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-bold text-content-muted uppercase tracking-widest">{label}</label>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => onToggle(name)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-600 text-brand-fg text-xs font-bold hover:bg-brand-700 transition-all"
            >
              {name} <X size={12} />
            </button>
          ))}
        </div>
      )}

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un salarié..."
          className="w-full bg-surface-soft border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-content outline-none focus:border-brand-500 transition-colors"
        />
      </div>

      <div className="max-h-36 overflow-y-auto space-y-1 border border-border rounded-xl p-1.5">
        {filtered.length === 0 ? (
          <p className="text-xs text-content-muted px-2 py-2">Aucun salarié trouvé.</p>
        ) : (
          filtered.map((emp) => {
            const name = employeeName(emp);
            const isSelected = selected.includes(name);
            return (
              <button
                key={emp.id}
                type="button"
                onClick={() => onToggle(name)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium text-left transition-all ${
                  isSelected ? "bg-brand-500/10 text-brand-600 dark:text-brand-400" : "text-content hover:bg-surface-soft"
                }`}
              >
                <span className="truncate">{name}</span>
                {isSelected && <Check size={14} className="shrink-0" />}
              </button>
            );
          })
        )}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={customName}
          onChange={(e) => setCustomName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddCustom();
            }
          }}
          placeholder="Ajouter un nom libre..."
          className="flex-1 bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500 transition-colors"
        />
        <button
          type="button"
          onClick={handleAddCustom}
          disabled={!customName.trim()}
          className="flex items-center gap-1 px-3 py-2 rounded-xl bg-surface-soft hover:bg-border border border-border text-content text-xs font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          <Plus size={14} /> Ajouter
        </button>
      </div>
    </div>
  );
}

export function BoardSettingsModal({
  onClose,
  initialTitle,
  initialTakenBy,
  initialCashedBy,
  onSave,
}: {
  onClose: () => void;
  initialTitle: string;
  initialTakenBy: string[];
  initialCashedBy: string[];
  onSave: (settings: { title: string; takenByOptions: string[]; cashedByOptions: string[] }) => void;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [takenBy, setTakenBy] = useState<string[]>(initialTakenBy);
  const [cashedBy, setCashedBy] = useState<string[]>(initialCashedBy);
  const [employees, setEmployees] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const data = await moduleApi.getEmployes();
        if (Array.isArray(data)) setEmployees(data);
      } catch (e) {
        console.error("Failed to load employees", e);
      }
    })();
  }, []);

  const toggleTakenBy = (name: string) => {
    setTakenBy((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };
  const toggleCashedBy = (name: string) => {
    setCashedBy((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };
  const addTakenBy = (name: string) => {
    setTakenBy((prev) => (prev.includes(name) ? prev : [...prev, name]));
  };
  const addCashedBy = (name: string) => {
    setCashedBy((prev) => (prev.includes(name) ? prev : [...prev, name]));
  };

  const handleSave = () => {
    onSave({
      title: title.trim() || initialTitle,
      takenByOptions: takenBy,
      cashedByOptions: cashedBy,
    });
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-surface border border-border rounded-3xl w-full max-w-lg max-h-[90vh] shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-600 dark:text-brand-400">
                <Settings size={18} />
              </div>
              <h3 className="text-lg font-bold text-content">Paramètres du tableau</h3>
            </div>
            <button onClick={onClose} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft transition-all">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 space-y-5 overflow-y-auto">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Titre du tableau</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-surface-soft border border-border rounded-xl px-4 py-3 text-content outline-none focus:border-brand-500 transition-colors"
              />
            </div>

            <StaffPicker label="Prise par" selected={takenBy} onToggle={toggleTakenBy} onAdd={addTakenBy} employees={employees} />
            <StaffPicker label="Encaissé par" selected={cashedBy} onToggle={toggleCashedBy} onAdd={addCashedBy} employees={employees} />
          </div>

          <div className="flex justify-end gap-3 p-6 border-t border-border shrink-0">
            <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">
              Annuler
            </button>
            <button onClick={handleSave} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">
              <Save size={15} /> Enregistrer
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
